/**
 * 工具相关类型定义
 * 基于 product/protocol/data-models.md
 */

/**
 * 工具分类（后端使用中文）
 * 注意："其他" 作为兜底分类，用于不好归类的工具
 */
export type ToolCategory = '通用工具' | 'API工具' | '数据工具' | '业务工具' | '其他'

/**
 * 工具分类映射（用于前端显示和筛选）
 */
export const TOOL_CATEGORY_NAMES: Record<ToolCategory, string> = {
  '通用工具': '通用工具',
  'API工具': 'API工具',
  '数据工具': '数据工具',
  '业务工具': '业务工具',
  '其他': '其他',
}

/**
 * MCP服务器配置
 */
export interface MCPServer {
  type: 'stdio' | 'sse' | 'http'
  url: string
  auth?: {
    type: 'bearer' | 'api_key' | 'none'
    value?: string
  }
}

/**
 * 工具参数定义
 */
export interface ToolParameters {
  input: {
    type: string
    properties: Record<string, any>
    required?: string[]
  }
  output: {
    type: string
    properties: Record<string, any>
  }
}

/**
 * 工具使用情况
 */
export interface ToolUsage {
  id: string // usage_xxx
  tool_id: string
  project_id: string
  agent_id: string
  used_at: string
  use_count: number
  last_used_at: string
}

/**
 * 工具信息（完整）
 */
export interface Tool {
  id: string // tool_xxx
  name: string
  description: string
  category: ToolCategory
  tags: string[]
  mcp_server: MCPServer
  parameters: ToolParameters
  config_source?: string
  usage_count?: number // 使用次数（列表时返回）
  usage_list?: ToolUsage[] // 使用情况列表（详情时返回）
  created_at: string // ISO 8601
  updated_at: string
  created_by?: string // user_id
}

/**
 * 工具参数属性定义（用于详情展示）
 */
export interface ToolParameterProperty {
  type: string
  description?: string
  enum?: string[]
  default?: unknown
}

/**
 * 工具列表项（包含 MCP 工具的扩展字段）
 */
export interface ToolListItem {
  id: string
  name: string
  description: string
  category: string
  tags: string[]
  created_at: string
  updated_at: string
  // MCP 工具扩展字段
  server_name?: string // MCP server 名称（用于分组）
  parameters?: {
    type?: string
    properties?: Record<string, ToolParameterProperty>
    required?: string[]
  }
  mcp_server?: MCPServer
}

/**
 * 创建工具请求
 */
export interface CreateToolRequest {
  name: string
  description: string
  category: ToolCategory
  mcp_server: MCPServer
  parameters: ToolParameters
  tags?: string[]
}

/**
 * 更新工具请求
 */
export interface UpdateToolRequest {
  name?: string
  description?: string
  category?: ToolCategory
  mcp_server?: MCPServer
  parameters?: ToolParameters
  tags?: string[]
}

/**
 * 工具列表查询参数
 * 注意：使用游标分页（Cursor Pagination），不支持 page/page_size
 */
export interface ToolListQuery {
  category?: ToolCategory
  search?: string // 搜索关键词（工具名称、描述）
  limit?: number // 每页数量，默认 100，最大 200
  last_key?: string // 分页游标（用于获取下一页）
}

/**
 * Builtin Tool 分类
 */
export type BuiltinToolCategory = 'heartbeat' | 'workspace' | 'skills' | 'code'

/**
 * Builtin Tool 可用性
 */
export type BuiltinToolAvailability = 'always' | 'conditional'

/**
 * Builtin Tool 信息
 */
export interface BuiltinToolInfo {
  name: string
  description: string
  category: BuiltinToolCategory
  availability: BuiltinToolAvailability
  condition: string // 可用性条件描述
  parameters: Record<string, any> // OpenAI function calling 格式的参数 schema
}

/**
 * Builtin Tools 列表响应
 */
export interface BuiltinToolsResponse {
  tools: BuiltinToolInfo[]
  total: number
  by_category: Record<BuiltinToolCategory, number> // 按分类统计
}

